import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { DialogService } from 'src/app/service/dialog.service';
import { NavigationService } from 'src/app/service/navigation.service';
import { AppScanEventType, ScanCameraService } from 'src/app/service/scan-camera-service.service';
import { AppScanResultMessage } from 'src/app/types/app.types';

@Component({
    selector: 'request-uid-scanning',
    templateUrl: './request-uid-scanning.component.html',
    styleUrls: ['./request-uid-scanning.component.scss'],
    providers: [ScanCameraService],
})
export class RequestUidScanningComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('videoBackground') videoBackground: ElementRef<HTMLVideoElement> | undefined;
    @ViewChild('cameraOutput') cameraOutput: ElementRef<HTMLDivElement> | undefined;

    permissionsError: boolean = false;
    message: AppScanResultMessage | null = null;
    readonly initialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private backgroundVideoStream: MediaStream | undefined;
    private readonly dead$ = new Subject<void>();

    constructor(
        private readonly navigationService: NavigationService,
        private readonly scanCameraService: ScanCameraService,
        private readonly appService: AppService,
        private readonly dialogService: DialogService
    ) {}

    ngOnInit(): void {
        this.navigationService.activity.reset();
        this.navigationService.activity.toolbar.hide();
    }

    ngAfterViewInit(): void {
        this.initializeVideoStream();
    }

    ngOnDestroy(): void {
        this.dead$.next();
        this.dead$.complete();

        if (this.backgroundVideoStream) {
            this.backgroundVideoStream.getTracks().forEach((track) => track.stop());
        }
    }

    private initializeVideoStream(): void {
        this.navigationService.activity.loader.show('Инициализация камеры');

        if (navigator?.mediaDevices) {
            navigator.mediaDevices
                .getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
                .then((stream) => {
                    this.navigationService.activity.loader.hide();
                    this.backgroundVideoStream = stream;

                    const videoBackgroundElement = this.videoBackground?.nativeElement;
                    const streamTracks = this.backgroundVideoStream.getVideoTracks();

                    if (videoBackgroundElement) {
                        videoBackgroundElement.srcObject = this.backgroundVideoStream;
                        videoBackgroundElement.onloadedmetadata = () => {
                            videoBackgroundElement.play();
                        };
                    }

                    if (streamTracks.length === 1) {
                        this.scanCameraService.initialize('camera', streamTracks[0].getSettings().deviceId ?? '');
                        this.initializeScanEmitter();
                    }
                })
                .catch((error) => {
                    this.message = { text: error, type: 'warning' };
                    this.closePageWithError('Камера не найдена или занята другим приложением');
                });
        } else {
            this.permissionsError = true;
            this.closePageWithError('Камера недоступна. Возможно вы пользуетесь незащищенным соединением');
        }
    }

    private initializeScanEmitter(): void {
        this.scanCameraService.emitter$.pipe(takeUntil(this.dead$)).subscribe({
            next: (event) => {
                switch (event.type) {
                    case AppScanEventType.Initialized:
                        this.initialized$.next(true);
                        this.permissionsError = false;
                        break;
                    case AppScanEventType.Scanning:
                        this.message = { text: 'Сканирование', type: 'process' };
                        break;
                    case AppScanEventType.Success:
                        this.scanCameraService.pause(true);
                        const divsInCamElement = this.cameraOutput?.nativeElement.getElementsByTagName('div');

                        if (divsInCamElement) {
                            for (let i = 0; i < divsInCamElement.length; i++) {
                                if (divsInCamElement[i].textContent == 'Scanner paused') {
                                    divsInCamElement[i].remove();
                                    break;
                                }
                            }
                        }

                        if (event.value) {
                            const result = event.value?.replace(/^([^=]*)+([=])/g, '');
                            if (!result.match(/^\d{25}$/)) {
                                this.message = { text: 'Неверный формат номера заявления', type: 'warning' };
                                this.scanCameraService.resume();
                            } else {
                                this.message = { text: `Верные данные: ${result}`, type: 'success' };
                                if (this.appService.subscriptionData) {
                                    this.appService.subscriptionData.sequence = result;
                                }
                                this.navigateBack();
                            }
                        }
                        break;
                }
            },
        });
    }

    navigateBack(): void {
        this.navigationService.navigateBack();
    }

    private closePageWithError(error?: string): void {
        this.navigationService.activity.loader.hide();
        this.dialogService.notifyInSnackBar(error ?? `Неизвестная ошибка: ${error}`, 'error');
        this.navigateBack();
    }
}
